import { useState } from "react";
import { ErrorMessage, FormikProvider, useFormik } from "formik";
import {
  getValidationSchema,
  initialValues as initialValuesBase,
} from "./config";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { StyledFormicError } from "../CreateOrEditTagModal/styles";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import CircularProgress from "@mui/material/CircularProgress";
import { green } from "@mui/material/colors";
import { Badge } from "types/badge";
import { useMutation } from "@apollo/client";
import { ADD_BADGE, UPDATE_BADGE } from "graphql/badge/badge.mutation";
import { Modal } from "components/Modal";
import { NotificationSeverity, BadgeCriteriaType } from "helpers/constants";
import { useNotifications } from "hooks/useNotifications";
import { OrganizationUploadImage } from "components/Input/OrganizationUploadImage";
import { useAppDispatch } from "hooks/useAppDispatch";

export type Props = {
  createOrEditBadgeModal: boolean;
  handleCreateOrEditBadgeModalDismiss: () => void;
  handleCreateOrEditBadgeChange: () => void;
  badge?: Badge | null;
};

const CreateOrEditBadgeModal = ({
  createOrEditBadgeModal,
  handleCreateOrEditBadgeModalDismiss,
  handleCreateOrEditBadgeChange,
  badge = null,
}: Props) => {
  const isEditMode = badge !== null;
  const [loading, setLoading] = useState(false);
  const [addBadge] = useMutation(ADD_BADGE);
  const [updateBadge] = useMutation(UPDATE_BADGE);
  const { notify } = useNotifications();
  const dispatch = useAppDispatch();
  const [iconUrl, setIconUrl] = useState(badge?.icon_url || "");

  const handleCreateOrEditBadge = async (reqValues: any) => {
    setLoading(true);

    try {
      await addBadge({
        variables: {
          name: reqValues.badgeName,
          description: reqValues.badgeDescription || null,
          icon_url: iconUrl || null,
          criteria_type: reqValues.criteriaType,
          criteria_value:
            typeof reqValues.criteriaValue === "number"
              ? reqValues.criteriaValue
              : parseInt(reqValues.criteriaValue, 10),
        },
      });
      handleCreateOrEditBadgeChange();
      setLoading(false);
      notify({
        message: "Badge created successfully",
        severity: NotificationSeverity.success,
      });
    } catch (error: any) {
      setLoading(false);
      notify({
        message: error.message || "Error creating badge",
        severity: NotificationSeverity.error,
      });
    }
  };

  const handleEditBadge = async (reqValues: any) => {
    setLoading(true);

    try {
      await updateBadge({
        variables: {
          id: badge?.badge_id || "",
          badge: {
            name: reqValues.badgeName,
            description: reqValues.badgeDescription || null,
            icon_url: iconUrl || null,
            criteria_type: reqValues.criteriaType,
            criteria_value:
              typeof reqValues.criteriaValue === "number"
                ? reqValues.criteriaValue
                : parseInt(reqValues.criteriaValue, 10),
          },
        },
      });
      handleCreateOrEditBadgeChange();
      setLoading(false);
      notify({
        message: "Badge updated successfully",
        severity: NotificationSeverity.success,
      });
    } catch (error: any) {
      setLoading(false);
      notify({
        message: error.message || "Error updating badge",
        severity: NotificationSeverity.error,
      });
    }
  };

  const initialValues = isEditMode
    ? {
        badgeName: badge?.name || "",
        badgeDescription: badge?.description || "",
        badgeIconUrl: badge?.icon_url || "",
        criteriaType: badge?.criteria_type || BadgeCriteriaType.FEEDBACK_COUNT,
        criteriaValue: badge?.criteria_value || 0,
      }
    : initialValuesBase;

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: getValidationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      if (isEditMode) {
        handleEditBadge(values).then();
      } else {
        handleCreateOrEditBadge(values).then();
      }
    },
  });

  const handleIconUpload = (filePath: string) => {
    setIconUrl(filePath);
  };

  return (
    <Modal
      open={createOrEditBadgeModal}
      onClose={handleCreateOrEditBadgeModalDismiss}
      title={isEditMode ? badge?.name : "Create a new Badge"}
      size="small"
    >
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <Box sx={{ mt: 1 }}>
            <Box>
              <TextField
                variant="standard"
                onChange={formik.handleChange}
                margin="dense"
                fullWidth
                name="badgeName"
                label="Badge Name"
                type="text"
                id="badgeName"
                value={formik?.values?.badgeName}
              />
              <ErrorMessage name="badgeName" component={StyledFormicError} />
            </Box>

            <Box>
              <TextField
                variant="standard"
                onChange={formik.handleChange}
                margin="dense"
                fullWidth
                name="badgeDescription"
                label="Description"
                type="text"
                id="badgeDescription"
                multiline
                rows={3}
                value={formik?.values?.badgeDescription}
              />
              <ErrorMessage
                name="badgeDescription"
                component={StyledFormicError}
              />
            </Box>

            <Box mt={2}>
              <InputLabel>Badge Icon</InputLabel>
              <OrganizationUploadImage
                img={iconUrl}
                onUploadedFile={handleIconUpload}
              />
            </Box>

            <Box>
              <FormControl variant="standard" fullWidth margin="dense">
                <InputLabel id="criteriaTypeLabel">Criteria Type</InputLabel>
                <Select
                  labelId="criteriaTypeLabel"
                  id="criteriaType"
                  name="criteriaType"
                  value={formik?.values?.criteriaType}
                  onChange={formik.handleChange}
                  label="Criteria Type"
                  variant="filled"
                >
                  <MenuItem value={BadgeCriteriaType.FEEDBACK_COUNT}>Feedback Count</MenuItem>
                  <MenuItem value={BadgeCriteriaType.FEEDBACK_REQUESTED_COUNT}>Feedback Request Count</MenuItem>
                </Select>
              </FormControl>
              <ErrorMessage name="criteriaType" component={StyledFormicError} />
            </Box>

            <Box>
              <TextField
                variant="standard"
                onChange={formik.handleChange}
                margin="dense"
                fullWidth
                name="criteriaValue"
                label="Criteria Value (e.g., feedback count)"
                type="number"
                id="criteriaValue"
                value={formik?.values?.criteriaValue}
                inputProps={{ min: 1 }}
              />
              <ErrorMessage
                name="criteriaValue"
                component={StyledFormicError}
              />
            </Box>

            <Grid sx={{ mt: 5, display: "flex", justifyContent: "flex-end" }}>
              <Box mr={2}>
                <Button
                  variant="outlined"
                  onClick={handleCreateOrEditBadgeModalDismiss}
                >
                  Cancel
                </Button>
              </Box>
              <Box>
                <Button
                  disabled={loading}
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  Submit
                </Button>
              </Box>
              {loading && (
                <CircularProgress
                  size={30}
                  sx={{
                    color: green[500],
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Grid>
          </Box>
        </form>
      </FormikProvider>
    </Modal>
  );
};

export default CreateOrEditBadgeModal;
